import { useState, type ReactNode } from "react"
import { track } from "@/lib/analytics"
import type { QuotaMetric } from "@/lib/quotaGate"
import { Overlay } from "../answer/Overlay"
import { useUpgrade, type UpgradeSource } from "./ProButton"
import "../answer/pro.css"
import "./pro-button.css"

export const FIRMS_CONTACT = "mailto:contact@nitiassistai.com?subject=Nira%20for%20firms"
/** What a firm buys Nira for firms for, said the same way wherever it is offered. */
export const FIRMS_LINE =
  "For a firm: seats and shared workspaces, the workbench for every client's notices and deadlines, your data held under your control, and exposure intelligence connected to your books."

/**
 * The one upgrade overlay (conversion.md): it opens only when the reader presses something gated,
 * never on load, scroll or a timer. Its title names what they pressed; two cards at most, Nira Pro
 * and Nira for firms; Not now closes it and leaves the page where it was.
 */
export function ProOverlay({ title, line, metric, source, onClose }: {
  title: string
  /** One paragraph on what Pro adds for the thing pressed. */
  line: string
  metric: QuotaMetric
  source: UpgradeSource
  onClose: () => void
}) {
  const { go, busy } = useUpgrade(source, metric, false, "overlay")
  return (
    <Overlay label={title} onClose={onClose}>
      <h2 className="nr-ov-h">{title}</h2>
      <p className="nr-ov-p">{line}</p>
      <div className="nup-choices">
        <div className="nup-choice">
          <b>Nira Pro</b>
          <span>Every notice and order read the way the officer will, with your documents.</span>
          <button type="button" className="ncf-btn primary" disabled={busy} onClick={() => void go()}>{busy ? "Opening checkout…" : "Start Pro"}</button>
        </div>
        <div className="nup-choice">
          <b>Nira for firms</b>
          <span>{FIRMS_LINE}</span>
          <a className="ncf-btn" href={FIRMS_CONTACT} onClick={() => track("firms_contact_clicked", { source })}>Talk to us</a>
        </div>
      </div>
      <button type="button" className="pro-link nup-later" onClick={onClose}>Not now</button>
    </Overlay>
  )
}

/**
 * A gated door: what it opens, said plainly, with the tier chip ("Pro", or "Firms" for what a firm
 * buys). Pressing it opens the overlay above; nothing else does.
 */
export function ProDoor({ children, title, line, metric, source, tier = "Pro", className = "npro-door", badge = true }: {
  children: ReactNode
  title: string
  line: string
  metric: QuotaMetric
  source: UpgradeSource
  tier?: "Pro" | "Firms"
  className?: string
  /** The tier chip; off where the door already says it is locked (a rail item). */
  badge?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className={className} onClick={() => { track("upgrade_clicked", { source, metric, plan: tier === "Pro" ? "pro" : "firms", step: "door" }); setOpen(true) }}>
        {children}{badge ? <span className="npro-badge sm">{tier}</span> : null}
      </button>
      {open ? <ProOverlay title={title} line={line} metric={metric} source={source} onClose={() => setOpen(false)} /> : null}
    </>
  )
}
